import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "user";
  active?: boolean;
  tenantId?: string;
}

export function useUsers(tenantId?: string) {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, "users");
        let usersQuery;

        // For legacy tenant, fetch all users and filter client-side
        // For new tenants, filter by tenantId
        if (tenantId === "legacy") {
          usersQuery = query(
            usersCollection,
            where("role", "in", ["admin", "user"])
          );
        } else if (tenantId) {
          usersQuery = query(
            usersCollection,
            where("tenantId", "==", tenantId),
            where("role", "in", ["admin", "user"])
          );
        } else {
          // Fallback: fetch all users if no tenantId provided
          usersQuery = query(
            usersCollection,
            where("role", "in", ["admin", "user"])
          );
        }

        const querySnapshot = await getDocs(usersQuery);
        const usersList = querySnapshot.docs.map(doc => {
          const data = doc.data() as any;
          return {
            id: doc.id,
            name: data.name,
            email: data.email,
            role: data.role,
            active: data.active !== undefined ? data.active : true,
            tenantId: data.tenantId
          };
        }).filter(user => {
          // For legacy tenant, show users without tenantId or with tenantId="legacy"
          if (tenantId === "legacy") {
            if (user.tenantId && user.tenantId !== "legacy") {
              return false; // Exclude users from other tenants
            }
          }
          return true;
        }) as User[];

        setUsers(usersList);
        setError(null);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch users"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [tenantId]);

  return { users, isLoading, error };
} 
